import {
    useEffect,
    useState,
    useRef,
    type FC,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    type TextInputProps,
    ScrollView,
    TextInput,
    Platform,
    View
} from "react-native";
import type IMarkdownEditorProps from "./type";
import type {
    MarkdownEditorKeyPressEvent,
    MarkdownEditorInlineFormat,
    MarkdownEditorAlignment,
    MarkdownEditorSelection,
    MarkdownEditorTextStyle,
    MarkdownEditorListKind,
    MarkdownEditorEdit,
    MarkdownEditorMode
} from "./type";
import stylesheet, {
    getMarkdownEditorMeasures,
    useStyles
} from "./stylesheet";
import MarkdownEditorToolbar from "./components/toolbar";
import {
    getMarkdownEditorActiveFormats,
    applyMarkdownEditorTextStyle,
    applyMarkdownEditorAlignment,
    createMarkdownEditorHistory,
    toggleMarkdownEditorInline,
    pushMarkdownEditorHistory,
    undoMarkdownEditorHistory,
    redoMarkdownEditorHistory,
    toggleMarkdownEditorQuote,
    insertMarkdownEditorImage,
    insertMarkdownEditorLink,
    toggleMarkdownEditorList,
    getMarkdownEditorStats,
    getMarkdownEditorMode,
    MARKDOWN_EDITOR_MODES
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    usePointerType,
    useBreakpoint
} from "../../helpers/pointer";
import type {
    FieldInputRenderPropsType
} from "../fieldControl/type";
import {
    formatNumber
} from "../../helpers/locale";
import {
    webStyle
} from "../../utils";
import MarkdownViewer from "../markdownViewer";
import FieldControl from "../fieldControl";
import Tabs from "../tabs";
import Text from "../text";
import Kbd from "../kbd";

const MarkdownEditor: FC<IMarkdownEditorProps> = ({
    onSelectionChange: onSelectionChangeProp,
    onChangeText: onChangeTextProp,
    spreadBehaviour = "stretch",
    onKeyPress: onKeyPressProp,
    isEditorResizable = true,
    isViewerResizable = true,
    toolbarContainerStyle,
    defaultMode = "live",
    onFocus: onFocusProp,
    syntax = "standard",
    onBlur: onBlurProp,
    initialValue = "",
    value: valueProp,
    editable = true,
    customLocalize,
    mode: modeProp,
    onImageInsert,
    onModeChange,
    customTheme,
    editorStyle,
    variants,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        shadowAlpha,
        elevations,
        typography,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const breakpoint = useBreakpoint();

    const inputID = useId();

    const helperID = `${inputID}-helper`;

    const [
        textState,
        setTextState
    ] = useState<string>(initialValue);

    const [
        modeState,
        setModeState
    ] = useState<MarkdownEditorMode>(defaultMode);

    const [
        selection,
        setSelection
    ] = useState<MarkdownEditorSelection | null>(null);

    const [
        pendingSelection,
        setPendingSelection
    ] = useState<MarkdownEditorSelection | undefined>(undefined);

    const [
        isFocused,
        setIsFocused
    ] = useState<boolean>(false);

    const [
        containerWidth,
        setContainerWidth
    ] = useState<number>(0);

    const historyRef = useRef(createMarkdownEditorHistory());

    const previewScrollRef = useRef<ScrollView>(null);

    const inputRef = useRef<TextInput>(null);

    const isTextControlled = valueProp !== undefined;

    const isModeControlled = modeProp !== undefined;

    const text = isTextControlled ? valueProp : textState;

    const mode = isModeControlled ? modeProp : modeState;

    const currentSelection: MarkdownEditorSelection = selection ?? {
        start: text.length,
        end: text.length
    };

    const isNarrow = containerWidth > 0 ? containerWidth < shapeConfigs.wideBreakpoint : breakpoint === "compact";

    const isToolbarAtBottom = pointerType === "coarse";

    const isShortcutHintVisible = pointerType === "fine";

    const isEditable = editable !== false;

    const isEditingMode = mode !== "preview";

    const activeFormats = getMarkdownEditorActiveFormats({
        text: selection ? text : "",
        selection: currentSelection
    });

    const stats = getMarkdownEditorStats({
        position: currentSelection.start,
        text
    });

    const measures = getMarkdownEditorMeasures({
        typography,
        borders,
        spaces
    });

    const {
        editorInput: editorInputDynamicStyle,
        previewLive: previewLiveDynamicStyle,
        previewPane: previewPaneDynamicStyle,
        helperKeys: helperKeysDynamicStyle,
        container: containerDynamicStyle,
        header: headerDynamicStyle,
        helper: helperDynamicStyle,
        status: statusDynamicStyle,
        field: fieldDynamicStyle
    } = useStyles({
        userStyle: style,
        spreadBehaviour,
        shapeConfigs,
        pointerType,
        shadowAlpha,
        elevations,
        typography,
        isNarrow,
        radiuses,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        if(!pendingSelection) {
            return;
        }

        const timeout = setTimeout(() => {
            setPendingSelection(undefined);
        }, 50);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        pendingSelection
    ]);

    const handleApply = (nextText: string, nextSelection?: MarkdownEditorSelection) => {
        if(!isTextControlled) {
            setTextState(nextText);
        }

        if(nextSelection) {
            setSelection(nextSelection);

            setPendingSelection(nextSelection);
        }

        onChangeTextProp?.(nextText);
    };

    const handleCommit = (edit: MarkdownEditorEdit) => {
        pushMarkdownEditorHistory({
            typingWindow: durations.typeahead,
            snapshot: {
                selection: currentSelection,
                text
            },
            history: historyRef.current,
            now: Date.now(),
            isTyping: false
        });

        handleApply(edit.text, edit.selection);

        inputRef.current?.focus();
    };

    const handleInlineToggle = (format: MarkdownEditorInlineFormat) => {
        handleCommit(toggleMarkdownEditorInline({
            selection: currentSelection,
            format,
            text
        }));
    };

    const handleListToggle = (kind: MarkdownEditorListKind) => {
        handleCommit(toggleMarkdownEditorList({
            selection: currentSelection,
            kind,
            text
        }));
    };

    const handleTextStyleChange = (textStyle: MarkdownEditorTextStyle) => {
        handleCommit(applyMarkdownEditorTextStyle({
            selection: currentSelection,
            textStyle,
            text
        }));
    };

    const handleLink = () => {
        handleCommit(insertMarkdownEditorLink({
            selection: currentSelection,
            text
        }));
    };

    const handleQuote = () => {
        handleCommit(toggleMarkdownEditorQuote({
            selection: currentSelection,
            text
        }));
    };

    const handleAlign = (alignment: MarkdownEditorAlignment) => {
        handleCommit(applyMarkdownEditorAlignment({
            selection: currentSelection,
            alignment,
            text
        }));
    };

    const handleImage = async () => {
        const capturedSelection = currentSelection;

        const capturedText = text;

        const image = await onImageInsert?.();

        if(!image) {
            inputRef.current?.focus();

            return;
        }

        handleCommit(insertMarkdownEditorImage({
            selection: capturedSelection,
            text: capturedText,
            image
        }));
    };

    const handleUndo = () => {
        const snapshot = undoMarkdownEditorHistory({
            current: {
                selection: currentSelection,
                text
            },
            history: historyRef.current
        });

        if(!snapshot) {
            return;
        }

        handleApply(snapshot.text, snapshot.selection);

        inputRef.current?.focus();
    };

    const handleRedo = () => {
        const snapshot = redoMarkdownEditorHistory({
            current: {
                selection: currentSelection,
                text
            },
            history: historyRef.current
        });

        if(!snapshot) {
            return;
        }

        handleApply(snapshot.text, snapshot.selection);

        inputRef.current?.focus();
    };

    const handleModeChange = (value: string) => {
        const nextMode = getMarkdownEditorMode(value);

        if(!nextMode) {
            return;
        }

        if(!isModeControlled) {
            setModeState(nextMode);
        }

        onModeChange?.(nextMode);
    };

    const onChangeText = (nextText: string) => {
        pushMarkdownEditorHistory({
            typingWindow: durations.typeahead,
            snapshot: {
                selection: currentSelection,
                text
            },
            history: historyRef.current,
            now: Date.now(),
            isTyping: true
        });

        handleApply(nextText);
    };

    const onSelectionChange: NonNullable<TextInputProps["onSelectionChange"]> = (event) => {
        setSelection(event.nativeEvent.selection);

        onSelectionChangeProp?.(event);
    };

    const onKeyPress = (event: MarkdownEditorKeyPressEvent) => {
        onKeyPressProp?.(event);

        const {
            shiftKey,
            ctrlKey,
            metaKey,
            key
        } = event.nativeEvent;

        if(!isEditable || !ctrlKey && !metaKey) {
            return;
        }

        const shortcuts: Record<string, () => void> = {
            u: () => handleInlineToggle("underline"),
            i: () => handleInlineToggle("italic"),
            z: shiftKey ? handleRedo : handleUndo,
            b: () => handleInlineToggle("bold"),
            y: handleRedo,
            k: handleLink
        };

        const handleShortcut = shortcuts[key.toLowerCase()];

        if(!handleShortcut) {
            return;
        }

        event.preventDefault();

        handleShortcut();
    };

    const onFocus: NonNullable<TextInputProps["onFocus"]> = (event) => {
        setIsFocused(true);

        onFocusProp?.(event);
    };

    const onBlur: NonNullable<TextInputProps["onBlur"]> = (event) => {
        setIsFocused(false);

        onBlurProp?.(event);
    };

    const onLayout = (event: LayoutChangeEvent) => {
        setContainerWidth(event.nativeEvent.layout.width);
    };

    const getStatusText = () => {
        if(!isEditingMode) {
            return localize("markdown-editor-view-only");
        }

        const wordsText = localize("markdown-editor-word-count", [
            formatNumber({
                locale: activeLocale,
                value: stats.words
            })
        ]);

        if(!isFocused) {
            return wordsText;
        }

        const cursorText = localize("code-cursor-position", [
            formatNumber({
                locale: activeLocale,
                value: stats.line
            }),
            formatNumber({
                locale: activeLocale,
                value: stats.column
            })
        ]);

        return `${wordsText} · ${cursorText}`;
    };

    const renderInput = ({
        style: inputFieldStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        const webProps = Platform.OS === "web" ? {
            dataSet: {
                ncoreShortcuts: "mod+B mod+I mod+U mod+K mod+Z mod+Shift+Z mod+Y"
            },
            "aria-describedby": helperID
        } : {};

        return <TextInput
            accessibilityLabel={props.accessibilityLabel ?? localize("markdown-editor-input")}
            aria-label={props["aria-label"] ?? localize("markdown-editor-input")}
            {...accessibilityProps}
            {...props}
            {...webProps}
            placeholderTextColor={placeholderTextColor}
            onSelectionChange={onSelectionChange}
            underlineColorAndroid="transparent"
            style={[
                inputFieldStyle,
                stylesheet.editorInput,
                editorInputDynamicStyle,
                isEditorResizable ? webStyle({
                    resize: "vertical"
                }) : {},
                editorStyle
            ]}
            selection={pendingSelection}
            onChangeText={onChangeText}
            onKeyPress={onKeyPress}
            editable={isEditable}
            onFocus={onFocus}
            multiline={true}
            onBlur={onBlur}
            ref={inputRef}
            value={text}
        />;
    };

    const renderHelper = () => {
        return <View
            style={[
                stylesheet.helper,
                helperDynamicStyle
            ]}
            id={helperID}
        >
            <Text
                customTheme={customTheme}
                variant="bodyMediumSize"
                color="mid"
            >
                {localize("markdown-editor-helper")}
            </Text>
            {
                isShortcutHintVisible ?
                    <>
                        <Text
                            customTheme={customTheme}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            ·
                        </Text>
                        <View
                            style={[
                                stylesheet.helperKeys,
                                helperKeysDynamicStyle
                            ]}
                        >
                            <Kbd
                                customLocalize={customLocalize}
                                customTheme={customTheme}
                                keys="mod+B"
                            />
                            <Text
                                customTheme={customTheme}
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {localize("markdown-editor-helper-bold")}
                            </Text>
                        </View>
                        <Text
                            customTheme={customTheme}
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            ·
                        </Text>
                        <View
                            style={[
                                stylesheet.helperKeys,
                                helperKeysDynamicStyle
                            ]}
                        >
                            <Kbd
                                customLocalize={customLocalize}
                                customTheme={customTheme}
                                keys="mod+I"
                            />
                            <Text
                                customTheme={customTheme}
                                variant="bodyMediumSize"
                                color="mid"
                            >
                                {localize("markdown-editor-helper-italic")}
                            </Text>
                        </View>
                    </>
                :
                    null
            }
        </View>;
    };

    const renderEditor = () => {
        return <View
            style={[
                !isNarrow && mode === "live" ? stylesheet.pane : undefined,
                fieldDynamicStyle
            ]}
        >
            <FieldControl
                minHeight={measures.controlHeight}
                customLocalize={customLocalize}
                spreadBehaviour="stretch"
                customTheme={customTheme}
                renderInput={renderInput}
                isReadOnly={!isEditable}
                isFocused={isFocused}
                isMultiline={true}
                inputID={inputID}
            />
            {renderHelper()}
        </View>;
    };

    const renderPreview = () => {
        const isLive = mode === "live";

        const viewer = <MarkdownViewer
            customLocalize={customLocalize}
            customTheme={customTheme}
            variants={variants}
            syntax={syntax}
            content={text}
        />;

        return <View
            style={[
                isLive && !isNarrow ? stylesheet.pane : undefined,
                isLive ? stylesheet.previewContainer : undefined,
                isLive ? previewLiveDynamicStyle : undefined,
                isLive && isViewerResizable ? webStyle({
                    resize: "vertical"
                }) : undefined,
                isLive ? previewPaneDynamicStyle : fieldDynamicStyle
            ]}
            aria-label={localize("preview")}
            role="region"
        >
            {
                isLive ?
                    <ScrollView
                        onContentSizeChange={() => {
                            if(!selection || currentSelection.start < text.length) {
                                return;
                            }

                            previewScrollRef.current?.scrollToEnd({
                                animated: false
                            });
                        }}
                        ref={previewScrollRef}
                    >
                        {viewer}
                    </ScrollView>
                :
                    viewer
            }
        </View>;
    };

    const renderBody = () => {
        if(mode === "write") {
            return renderEditor();
        }

        if(mode === "preview") {
            return renderPreview();
        }

        return <View
            style={isNarrow ? stylesheet.bodyStack : stylesheet.bodyWide}
        >
            {renderEditor()}
            {renderPreview()}
        </View>;
    };

    const renderToolbar = () => {
        return <MarkdownEditorToolbar
            onAlign={syntax === "legacy" ? handleAlign : undefined}
            onImage={onImageInsert ? handleImage : undefined}
            canRedo={historyRef.current.future.length > 0}
            canUndo={historyRef.current.past.length > 0}
            isDisabled={!isEditingMode || !isEditable}
            onTextStyleChange={handleTextStyleChange}
            onInlineToggle={handleInlineToggle}
            onListToggle={handleListToggle}
            customLocalize={customLocalize}
            isAtBottom={isToolbarAtBottom}
            style={toolbarContainerStyle}
            activeFormats={activeFormats}
            onQuoteToggle={handleQuote}
            returnFocusRef={inputRef}
            customTheme={customTheme}
            variants={variants}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onLink={handleLink}
        />;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        onLayout={onLayout}
    >
        <Tabs
            onValueChange={handleModeChange}
            customLocalize={customLocalize}
            customTheme={customTheme}
            variant="segment"
            value={mode}
        >
            <View
                style={[
                    stylesheet.header,
                    headerDynamicStyle
                ]}
            >
                <Tabs.List
                    title={localize("markdown-editor-mode")}
                    customTheme={customTheme}
                >
                    {
                        MARKDOWN_EDITOR_MODES.map((currentMode) => <Tabs.Trigger
                            customLocalize={customLocalize}
                            title={localize(currentMode)}
                            customTheme={customTheme}
                            value={currentMode}
                            key={currentMode}
                        />)
                    }
                </Tabs.List>
                <Text
                    customTheme={customTheme}
                    variant="bodySmallSize"
                    style={[
                        stylesheet.status,
                        statusDynamicStyle
                    ]}
                    color="low"
                >
                    {getStatusText()}
                </Text>
            </View>
            <Tabs.Content
                customTheme={customTheme}
                value={mode}
            >
                {isToolbarAtBottom ? null : renderToolbar()}
                {renderBody()}
                {isToolbarAtBottom ? renderToolbar() : null}
            </Tabs.Content>
        </Tabs>
    </View>;
};
export default MarkdownEditor;
